import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FEEDBACK } from "@/constants/testIds";
import { toast } from "sonner";
import { MessageSquare, X, Bug, Sparkles, Star, Send, BookOpen, Image as ImageIcon, Trash2 } from "lucide-react";

const KINDS = [
  { key: "general", label: "General", icon: MessageSquare, placeholder: "Tell us what you think…" },
  { key: "bug", label: "Bug", icon: Bug, placeholder: "What went wrong? Steps to reproduce…" },
  { key: "feature", label: "Feature", icon: Sparkles, placeholder: "What should we build?" },
  { key: "lesson", label: "Lesson", icon: BookOpen, placeholder: "How was this lesson? What was unclear?" },
];

function pickDeviceLabel() {
  const ua = navigator.userAgent || "";
  const w = window.innerWidth;
  if (/Mobi|Android|iPhone|iPad/i.test(ua)) return w < 768 ? "mobile" : "tablet";
  return "desktop";
}

async function screenshotFromScreenCapture() {
  // Uses the Screen Capture API — a single frame of the current tab, no plugins.
  if (!navigator.mediaDevices?.getDisplayMedia) throw new Error("Screen capture not supported");
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "browser" }, audio: false });
  const track = stream.getVideoTracks()[0];
  await new Promise((r) => setTimeout(r, 250));
  const video = document.createElement("video");
  video.srcObject = stream;
  await video.play();
  const canvas = document.createElement("canvas");
  const maxW = 1280;
  const scale = Math.min(1, maxW / video.videoWidth);
  canvas.width = Math.floor(video.videoWidth * scale);
  canvas.height = Math.floor(video.videoHeight * scale);
  canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
  track.stop();
  return canvas.toDataURL("image/jpeg", 0.7);
}

export function FeedbackWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("general");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [screenshot, setScreenshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const isLesson = /\/app\/paths\/[^/]+\/lessons\/\d+/.test(location.pathname);
  const context = useMemo(() => ({
    lesson_slug: isLesson ? params?.slug : null,
    lesson_index: isLesson ? Number(params?.index) : null,
  }), [isLesson, params]);

  useEffect(() => {
    // Reset draft when panel closes
    if (!open) { setScreenshot(null); }
  }, [open]);

  if (!user) return null;

  const submit = async () => {
    if (!message.trim() && kind !== "rating") { toast.error("Add a message"); return; }
    if (kind === "bug" && !message.trim().toLowerCase().includes(" ")) { toast.error("Add a bit more detail"); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        kind,
        message: message.trim() || `Rating ${rating}/5`,
        rating: rating || null,
        page: location.pathname,
        lesson_slug: context.lesson_slug,
        lesson_index: context.lesson_index,
        user_agent: navigator.userAgent.slice(0, 300),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        device: pickDeviceLabel(),
        screenshot: kind === "bug" ? screenshot : null,
      });
      toast.success("Thanks — feedback received");
      setMessage(""); setRating(0); setScreenshot(null); setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Screenshot too large (max 4MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <>
      <button
        data-testid={FEEDBACK.fabBtn}
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-40 grid h-12 w-12 place-items-center rounded-full border border-primary/40 bg-background/95 backdrop-blur shadow-[0_8px_24px_-4px_rgba(0,229,255,0.4)] hover:border-primary transition"
        aria-label="Send feedback"
      >
        {open ? <X className="h-5 w-5 text-primary" /> : <MessageSquare className="h-5 w-5 text-primary" />}
      </button>

      {open && (
        <div data-testid={FEEDBACK.panel} className="fixed bottom-24 right-6 z-40 w-[min(94vw,400px)] rounded-2xl border border-border bg-card p-4 shadow-2xl animate-fade-up">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="label-mono text-primary">// feedback hub</div>
              <div className="mt-1 font-display font-bold">Help us shape LaneX</div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {KINDS.map((k) => (
              <button
                key={k.key}
                data-testid={FEEDBACK.kind(k.key)}
                onClick={() => setKind(k.key)}
                className={`flex flex-col items-center gap-1 rounded-xl border p-2 text-xs transition ${kind === k.key ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"}`}
              >
                <k.icon className="h-4 w-4" />
                {k.label}
              </button>
            ))}
          </div>

          {kind === "lesson" && (
            <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-2 label-mono">
              {context.lesson_slug ? `on ${context.lesson_slug} · L${context.lesson_index + 1}` : "not on a lesson — open one to add context"}
              <div className="mt-2 flex gap-1">
                {[1,2,3,4,5].map((n) => (
                  <button
                    key={n}
                    data-testid={FEEDBACK.ratingStar(n)}
                    onClick={() => setRating(n)}
                    aria-label={`${n} star`}
                    className="rounded-full p-0.5"
                  >
                    <Star className={`h-5 w-5 ${n <= rating ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                  </button>
                ))}
              </div>
            </div>
          )}

          <Textarea
            data-testid={FEEDBACK.messageInput}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={(KINDS.find(k => k.key === kind) || {}).placeholder}
            className="mt-3 min-h-[100px] rounded-lg"
          />

          {kind === "bug" && (
            <div className="mt-3">
              <div className="mb-1 flex items-center justify-between">
                <div className="label-mono">// screenshot (optional)</div>
                {screenshot && (
                  <button data-testid="feedback-screenshot-remove" onClick={() => setScreenshot(null)} className="label-mono text-destructive inline-flex items-center gap-1">
                    <Trash2 className="h-3 w-3" /> remove
                  </button>
                )}
              </div>
              {screenshot ? (
                <img alt="preview" src={screenshot} className="h-24 w-full rounded-lg border border-border object-cover" />
              ) : (
                <div className="flex gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid="feedback-screenshot-input"
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    data-testid="feedback-screenshot-upload"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 label-mono hover:border-primary/50 transition"
                  >
                    <ImageIcon className="h-3.5 w-3.5" /> upload
                  </button>
                  <button
                    onClick={async () => {
                      try { const d = await screenshotFromScreenCapture(); setScreenshot(d); }
                      catch (e) { toast.error("Screen capture blocked — use upload"); }
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 label-mono hover:border-primary/50 transition"
                    data-testid="feedback-screenshot-capture"
                  >
                    capture tab
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex items-center justify-between">
            <div className="label-mono truncate">on {location.pathname}</div>
            <Button
              data-testid={FEEDBACK.submitBtn}
              onClick={submit}
              disabled={busy}
              size="sm"
              className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
            >
              <Send className="mr-2 h-3.5 w-3.5" /> {busy ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
